import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import LockIcon from '@mui/icons-material/Lock';

const API_BASE_URL = 'https://api.labs.jeshua.ong';

const GRUPOS = [
  {
    label: 'General',
    items: [
      { key: 'trabajadores-de-la-luz', label: 'Trabajadores de la Luz' },
      { key: 'gaia', label: 'Gaia' }
    ]
  },
  {
    label: 'Esenios',
    items: [
      { key: 'esenios-jeshua', label: 'Jeshua' },
      { key: 'esenios-maria-magdalena', label: 'María Magdalena' },
      { key: 'esenios-maria-madre', label: 'María Madre' },
      { key: 'esenios-abuela-ana', label: 'Abuela Ana' }
    ]
  },
  {
    label: 'Ángeles y Arcángeles',
    items: [
      { key: 'angeles-y-arcangeles/angeles', label: 'Ángeles' },
      { key: 'angeles-y-arcangeles/miguel', label: 'Arcángel Miguel' },
      { key: 'angeles-y-arcangeles/metatron', label: 'Arcángel Metatron' },
      { key: 'angeles-y-arcangeles/gabriel', label: 'Arcángel Gabriel' },
      { key: 'angeles-y-arcangeles/rafael', label: 'Arcángel Rafael' },
      { key: 'angeles-y-arcangeles/chamuel', label: 'Arcángel Chamuel' },
      { key: 'angeles-y-arcangeles/jofiel', label: 'Arcángel Jofiel' },
      { key: 'angeles-y-arcangeles/uriel', label: 'Arcángel Uriel' },
      { key: 'angeles-y-arcangeles/zadquiel', label: 'Arcángel Zadquiel' }
    ]
  }
];

const ALL_KEYS = GRUPOS.flatMap(g => g.items.map(i => i.key));

const checkboxSx = {
  color: 'rgba(255,255,255,0.4)',
  '&.Mui-checked': { color: '#ff5722' },
  '&.MuiCheckbox-indeterminate': { color: '#ff5722' },
  py: 0.5
};

const AdminPage = () => {
  const [selected, setSelected] = useState({});
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const allChecked = ALL_KEYS.every(k => selected[k]);
  const someChecked = ALL_KEYS.some(k => selected[k]);
  const selectedKeys = ALL_KEYS.filter(k => selected[k]);

  const handleToggle = (key) => {
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleToggleGroup = (items) => {
    const keys = items.map(i => i.key);
    const groupAllChecked = keys.every(k => selected[k]);
    setSelected(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = !groupAllChecked; });
      return next;
    });
  };

  const handleToggleAll = () => {
    const next = {};
    ALL_KEYS.forEach(k => { next[k] = !allChecked; });
    setSelected(next);
  };

  const handleSync = async () => {
    if (!password) { setError('Ingresa la contraseña de sincronización.'); return; }
    if (selectedKeys.length === 0) { setError('Selecciona al menos un canalizador.'); return; }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, canalizadores: selectedKeys })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Error al sincronizar.');
      } else {
        setResult(data);
      }
    } catch (e) {
      setError('Error de conexión con la API.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" sx={{ color: '#fff', mb: 1, fontWeight: 700 }}>
        Panel de Administración
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
        Sincroniza canalizaciones desde Google Drive hacia la base de datos.
      </Typography>

      <Paper sx={{
        p: 4,
        backgroundColor: 'rgba(255,255,255,0.04)',
        borderRadius: 2,
        border: '1px solid rgba(255,255,255,0.08)'
      }}>

        {/* Seleccionar todos */}
        <FormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked && !allChecked}
              onChange={handleToggleAll}
              sx={checkboxSx}
            />
          }
          label={
            <Typography sx={{ color: '#fff', fontWeight: 600 }}>
              Seleccionar todos
            </Typography>
          }
        />

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

        {/* Grupos de canalizadores */}
        {GRUPOS.map((grupo) => {
          const groupKeys = grupo.items.map(i => i.key);
          const groupAllChecked = groupKeys.every(k => selected[k]);
          const groupSomeChecked = groupKeys.some(k => selected[k]);

          return (
            <Box key={grupo.label} sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupAllChecked}
                    indeterminate={groupSomeChecked && !groupAllChecked}
                    onChange={() => handleToggleGroup(grupo.items)}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Typography sx={{
                    color: '#ff5722',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em'
                  }}>
                    {grupo.label}
                  </Typography>
                }
              />
              <FormGroup sx={{ pl: 4 }}>
                {grupo.items.map((item) => (
                  <FormControlLabel
                    key={item.key}
                    control={
                      <Checkbox
                        checked={!!selected[item.key]}
                        onChange={() => handleToggle(item.key)}
                        sx={checkboxSx}
                      />
                    }
                    label={
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                        {item.label}
                      </Typography>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          );
        })}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />

        {/* Contraseña y botón */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            type="password"
            label="Contraseña"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSync(); }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '1rem' }} />
                </InputAdornment>
              )
            }}
            sx={{
              flex: 1,
              '& .MuiInputBase-input': { color: '#fff' },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#ff5722' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#ff5722' }
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={loading || selectedKeys.length === 0}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            sx={{
              backgroundColor: '#ff5722',
              '&:hover': { backgroundColor: '#e64a19' },
              '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' },
              whiteSpace: 'nowrap',
              px: 3,
              height: 40
            }}
          >
            {loading ? 'Sincronizando...' : `Sincronizar${selectedKeys.length > 0 ? ` (${selectedKeys.length})` : ''}`}
          </Button>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{
            mt: 3,
            backgroundColor: 'rgba(211,47,47,0.12)',
            color: '#fff',
            border: '1px solid rgba(211,47,47,0.3)',
            '& .MuiAlert-icon': { color: '#f44336' }
          }}>
            {error}
          </Alert>
        )}

        {/* Resultado */}
        {result && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="success" sx={{
              mb: 2,
              backgroundColor: 'rgba(46,125,50,0.12)',
              color: '#fff',
              border: '1px solid rgba(46,125,50,0.3)',
              '& .MuiAlert-icon': { color: '#4caf50' }
            }}>
              Sincronización completada — {result.total_synced} canalizaciones procesadas
            </Alert>

            {(result.details || []).map((d, i) => (
              <Box key={i} sx={{
                mb: 1,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: `1px solid ${d.errors > 0 ? 'rgba(244,67,54,0.25)' : 'rgba(255,255,255,0.07)'}`
              }}>
                <Typography sx={{ color: '#ff5722', fontSize: '0.85rem', fontWeight: 600 }}>
                  {d.section}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                  {d.synced} canalizaciones · {d.languages} idiomas
                  {d.errors > 0 && ` · ${d.errors} error${d.errors > 1 ? 'es' : ''}`}
                </Typography>
                {d.error_detail && (
                  <Typography sx={{ color: '#f44336', fontSize: '0.75rem', mt: 0.5 }}>
                    {d.error_detail}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AdminPage;
